<template>
  <div class="flex flex-col">
    <!-- Back button (mobile only) -->
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-4 right-4 z-30"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="router.back()"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>
    <!-- Back button (desktop) -->
    <div
      v-if="!isMobileDevice"
      class="absolute flex items-center gap-2 px-4 pt-4"
    >
      <IconButton
        variant="text"
        size="sm"
        aria-label="Go back"
        @click="router.back()"
      >
        <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
      </IconButton>
    </div>
    <div class="flex-1 flex items-center justify-center">
      <div class="bg-surface rounded-2xl p-8 w-full max-w-md space-y-8 relative overflow-hidden text-text-primary">
        <!-- Background decoration -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full translate-y-1/2 -translate-x-1/2" />

        <!-- Content -->
        <div class="relative z-10">
          <div class="text-center mb-4">
            <h2 class="text-3xl font-bold">
              Welcome to DrumbleBee
            </h2>
            <p class="text-text-secondary mt-2">
              Sign in to access your music collection
            </p>
          </div>

          <div class="space-y-4">
            <!-- Main Buttons -->
            <div
              v-if="authStep === 'initial'"
              class="space-y-4"
            >
              <button
                v-if="isNativeIos"
                class="w-full bg-black text-white rounded-xl py-3 px-4 flex items-center justify-center space-x-3 hover:bg-gray-900 transition-colors relative overflow-hidden group"
                @click="handleAppleSignIn"
              >
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 814 1000"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49.1 190.5-49.1zm-5.8-181.1c34.8-41.4 58.3-98.8 58.3-156.2 0-8.4-.7-16.9-2.1-24.7-55.2 2.1-120.5 36.9-160.8 84.4-31.5 37.5-61.6 96.7-61.6 155.5 0 9 1.4 18.1 2.8 21 3.5.7 9 1.4 14.5 1.4 48.4 0 110.3-32.9 148.9-81.4z" />
                </svg>
                <span class="font-medium">Continue with Apple</span>
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </button>

              <button
                v-if="showGoogleLogin"
                class="w-full bg-white text-gray-800 rounded-xl py-3 px-4 flex items-center justify-center space-x-3 hover:bg-gray-100 transition-colors relative overflow-hidden group"
                @click="handleGoogleSignIn"
              >
                <img
                  src="/google.svg"
                  alt="Google"
                  class="w-6 h-6"
                >
                <span class="font-medium">Continue with Google</span>
              
                <!-- Hover effect -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </button>

              <div class="relative flex py-2 items-center">
                <div class="flex-grow border-t border-text-secondary/20" />
                <span class="flex-shrink-0 mx-4 text-text-secondary text-sm">Or</span>
                <div class="flex-grow border-t border-text-secondary/20" />
              </div>

              <button
                class="w-full bg-surface-hover text-text-primary border border-text-secondary/20 rounded-xl py-3 px-4 flex items-center justify-center space-x-3 hover:bg-surface-active transition-colors"
                @click="authStep = 'email_input'"
              >
                <span class="font-medium">Continue with Email</span>
              </button>
            </div>

            <!-- Email Input Step -->
            <div
              v-else-if="authStep === 'email_input'"
              class="space-y-4"
            >
              <div>
                <label class="block text-sm font-medium text-text-secondary mb-1">Email Address</label>
                <input 
                  v-model="email"
                  type="email" 
                  placeholder="Enter your email"
                  class="w-full bg-background border border-text-secondary/20 rounded-xl px-4 py-3 text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  @keyup.enter="handleSendOtp"
                >
              </div>
            
              <button
                class="w-full bg-primary text-text-primary rounded-xl py-3 px-4 font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="loading || !email"
                @click="handleSendOtp"
              >
                <span v-if="loading">Sending Code...</span>
                <span v-else>Send Login Code</span>
              </button>

              <button
                class="w-full text-text-secondary hover:text-text-primary transition-colors text-sm"
                @click="authStep = 'initial'"
              >
                Back to options
              </button>
            </div>

            <!-- OTP Input Step -->
            <div
              v-else-if="authStep === 'otp_verify'"
              class="space-y-4"
            >
              <div class="text-center mb-4">
                <p class="text-text-secondary text-sm">
                  Enter code sent to {{ email }}
                </p>
              </div>

              <div>
                <input 
                  v-model="otp"
                  type="text" 
                  placeholder="123456"
                  class="w-full bg-background border border-text-secondary/20 rounded-xl px-4 py-3 text-text-primary text-center tracking-widest text-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  @keyup.enter="handleVerifyOtp"
                >
              </div>

              <button
                class="w-full bg-primary text-text-primary rounded-xl py-3 px-4 font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="loading || !otp"
                @click="handleVerifyOtp"
              >
                <span v-if="loading">Verifying...</span>
                <span v-else>Verify & Login</span>
              </button>

              <div class="flex justify-between items-center text-sm pt-2">
                <button
                  class="text-text-secondary hover:text-text-primary transition-colors"
                  @click="authStep = 'email_input'"
                >
                  Change Email
                </button>
                <button
                  class="text-text-primary hover:text-primary-hover transition-colors disabled:text-text-secondary disabled:cursor-not-allowed"
                  :disabled="loading || resendCooldownSeconds > 0"
                  @click="handleSendOtp"
                >
                  <span v-if="resendCooldownSeconds > 0">Resend in {{ resendCooldownSeconds }}s</span>
                  <span v-else>Resend Code</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { useSupabaseAuth } from '../composables/useSupabaseAuth'
import { useToast } from '../composables/useToast'
import { useDeviceDetection } from '@/composables/useDeviceDetection'
import IconButton from '@/components/IconButton.vue'
import { ChevronLeftIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const route = useRoute()
const { isMobileDevice } = useDeviceDetection()
const isNativeIos = Capacitor.getPlatform() === 'ios'
const { signInWithApple, signInWithGoogle, signInWithOtp, verifyOtp, signOut } = useSupabaseAuth()
const { createToast } = useToast()

const showGoogleLogin = ref(import.meta.env.DEV)
const authStep = ref<'initial' | 'email_input' | 'otp_verify'>('initial')
const email = ref('')
const otp = ref('')
const loading = ref(false)
const resendCooldownSeconds = ref(0)
const RESEND_COOLDOWN_SECONDS = 30
let resendCooldownTimer: ReturnType<typeof setInterval> | null = null

const startResendCooldown = () => {
  if (resendCooldownTimer) {
    clearInterval(resendCooldownTimer)
  }

  resendCooldownSeconds.value = RESEND_COOLDOWN_SECONDS
  resendCooldownTimer = setInterval(() => {
    if (resendCooldownSeconds.value <= 1) {
      resendCooldownSeconds.value = 0
      if (resendCooldownTimer) {
        clearInterval(resendCooldownTimer)
        resendCooldownTimer = null
      }
      return
    }

    resendCooldownSeconds.value -= 1
  }, 1000)
}

onMounted(async () => {
  // Check if we're being asked to log out
  if (route.query.logout === 'true') {
    try {
      await signOut();
      createToast({
        type: 'success',
        message: 'You have been signed out'
      });
      
      // Remove logout parameter from URL
      router.replace({ query: {} });
    } catch (error) {
      createToast({
        type: 'error',
        message: 'Failed to sign out'
      });
    }
  }
})

const handleAppleSignIn = async () => {
  try {
    await signInWithApple()
    const redirectPath = route.query.redirect as string || '/'
    router.push(redirectPath)
  } catch (error) {
    createToast({
      type: 'error',
      message: 'Failed to sign in with Apple: ' + error
    })
  }
}

const handleGoogleSignIn = async () => {
  try {
    await signInWithGoogle()
    const redirectPath = route.query.redirect as string || '/'
    router.push(redirectPath)
  } catch (error) {
    createToast({
      type: 'error',
      message: 'Failed to sign in with Google: ' + error
    })
  }
}

const handleSendOtp = async () => {
  if (!email.value) return
  
  loading.value = true
  try {
    await signInWithOtp(email.value)
    authStep.value = 'otp_verify'
    startResendCooldown()
    createToast({
      type: 'success',
      message: 'Login code sent to your email'
    })
  } catch (error: any) {
    createToast({
      type: 'error',
      message: error.message || 'Failed to send login code'
    })
  } finally {
    loading.value = false
  }
}

const handleVerifyOtp = async () => {
  if (!otp.value) return

  loading.value = true
  try {
    await verifyOtp(email.value, otp.value)
    const redirectPath = route.query.redirect as string || '/'
    router.push(redirectPath)
    createToast({
      type: 'success',
      message: 'Successfully logged in'
    })
  } catch (error: any) {
    createToast({
      type: 'error',
      message: error.message || 'Invalid code'
    })
  } finally {
    loading.value = false
  }
}

onUnmounted(() => {
  if (resendCooldownTimer) {
    clearInterval(resendCooldownTimer)
    resendCooldownTimer = null
  }
})
</script>
